import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import WorkbookSettingsTab from '@/components/content-generator/editor/tabs/WorkbookSettingsTab';
import { TagFormDialog } from '@/components/content-generator/workbook';
import { useWorkbookOperations } from '@/hooks/content-generator/use-workbook-operations';
import { useWorkbookNavigation } from '@/hooks/nav';
import { useDeleteProjectWithGoal } from '@/hooks/content-generator/use-delete-project-with-goal';
import { usePageMutations } from '@/hooks/content-generator/pages';
import { useQuestDetection } from '@/hooks/onboarding/useQuestDetection';
import { useUser } from '@/hooks/auth/use-user';
import { supabase } from '@/integrations/supabase/client';

interface ProjectOutletContext {
  project: any;
  goal: any;
  agentThreadId?: string;
  navigationData: any;
}

/**
 * ProjectSettingsPage - Settings route for project system
 * Contains workbook settings and configuration
 */
const ProjectSettingsPage: React.FC = () => {
  const { project, goal, agentThreadId, navigationData } = useOutletContext<ProjectOutletContext>();
  const { user } = useUser();
  
  // Initialize quest detection for this page
  useQuestDetection();
  
  // Local state for tag creation modal
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isCheckingOwnership, setIsCheckingOwnership] = useState(true);
  
  // Use unified workbook operations with optimistic updates for better UX
  const { 
    toggleArchive,
    isArchiveToggling
  } = useWorkbookOperations({ optimisticUpdates: true });
  
  // Use workbook navigation for auto-redirect functionality
  const { deleteWorkbookWithImmediateRedirect, ConfirmationDialog } = useWorkbookNavigation();
  
  // Use the enhanced delete function that handles goals
  const deleteProjectWithGoal = useDeleteProjectWithGoal();
  
  const { 
    updatePageAsync
  } = usePageMutations();
  
  // Check if current user is the owner
  useEffect(() => {
    const checkOwnership = async () => {
      if (!project?.id || !user?.id) {
        setIsCheckingOwnership(false);
        return;
      }

      try {
        // Get editors to check ownership
        const { data: editorsData, error } = await supabase.rpc('workbook_collaboration', {
          p_action: 'get_editors',
          p_workbook_id: project.id
        });

        if (error) {
          console.error('Failed to check ownership:', error);
          setIsOwner(false);
        } else {
          // Check if current user is the owner
          const userIsOwner = editorsData?.some((editor: any) => 
            editor.id === user.id && editor.is_owner
          );
          setIsOwner(userIsOwner || false);
        }
      } catch (error) {
        console.error('Error checking ownership:', error);
        setIsOwner(false);
      } finally {
        setIsCheckingOwnership(false);
      }
    };

    checkOwnership();
  }, [project?.id, user?.id]);

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Project not found.
        </p>
      </div>
    );
  }
  
  const pages = navigationData?.pages || [];

  const handleToggleArchive = () => {
    if (project.id && !isArchiveToggling) {
      const currentArchivedState = project.is_archived || false;
      const newArchivedState = !currentArchivedState;
      console.log('[ProjectSettingsPage] Toggling archive:', project.id, 'from', currentArchivedState, 'to', newArchivedState);
      toggleArchive({ id: project.id, isArchived: newArchivedState });
    }
  };

  // Updated delete handler with beautiful confirmation dialog and goal cleanup
  const handleDelete = () => {
    if (project.id) {
      deleteWorkbookWithImmediateRedirect(async () => {
        await deleteProjectWithGoal.mutateAsync(project.id);
      });
    }
  };

  const handleCoverImageChange = async (url: string | undefined) => {
    if (project.id) {
      try {
        await updatePageAsync({ 
          id: project.id, 
          updates: { 
            metadata: { 
              ...project.metadata,
              coverImage: url 
            } 
          } 
        });
      } catch (error) {
        console.error('Failed to update cover image:', error);
      }
    }
  };

  const handleCreateTag = () => {
    setIsCreateTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setIsCreateTagModalOpen(false);
  };
  
  return (
    <>
      <div className="space-y-6">
        <WorkbookSettingsTab 
          pageId={project.id}
          title={project.title}
          isArchived={project.is_archived}
          coverImage={project.metadata?.coverImage}
          createdAt={project.created_at}
          updatedAt={project.updated_at}
          onToggleArchive={handleToggleArchive}
          onDelete={handleDelete}
          onCoverImageChange={handleCoverImageChange}
          onCreateTag={handleCreateTag}
          isArchiveToggling={isArchiveToggling}
          isDeleting={deleteProjectWithGoal.isPending}
          isOwner={isOwner}
          isCheckingOwnership={isCheckingOwnership}
        />
      </div>
      
      {/* Tag Creation Modal */}
      <TagFormDialog
        isOpen={isCreateTagModalOpen}
        onClose={handleCloseTagModal}
        mode="create"
      />
      
      {/* Confirmation Dialog for delete operations */}
      <ConfirmationDialog />
    </>
  );
};

export default ProjectSettingsPage; 